/**
 * @author       luis.orihuela@zumper.com (Luis Orihuela)
 * @copyright    Copyright (c) 2023, Zumper
 */
import { Box } from '@chakra-ui/react'
import { InputWithPopover } from '@zcode-ui/web-zumper-ui'
import { getPriceText } from '@zcode/nr/es/shared/bizos/rentable_utils'
import type { FloorplanListing, NormalFloorplanListingData } from '@zcode/state-type'
import { isTruthy } from '@zcode/utils'
import React, { useEffect, useMemo, useState } from 'react'
import {
  createFloorplansIdDict,
  groupFloorplansByBedsBaths,
  sortOptions,
} from '../../../../modules/savedSearchesView/thunks/createSearch/helpers'

interface Props {
  error?: string
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined
  hasMessaged?: boolean
  isDefault?: boolean
  isInvalid?: boolean
  isMobile: boolean
  isRequired?: boolean
  name?: string
  onChange: (e: React.ChangeEvent<HTMLSelectElement>) => void
  onlyShowFloorplanGroup?: boolean
  onTrackConversion: (event: React.ChangeEvent<HTMLSelectElement>) => void
  shouldChangeText?: boolean
  selectedFloorplanId: number | null | undefined
  selectedFloorplanFromUrl: FloorplanListing<NormalFloorplanListingData> | undefined
  setFloorplanFromParam: (param: undefined) => void
  setFieldValue: (
    key: 'selectedFloorplanBedroom' | 'selectedFloorplanMaxPrice' | 'selectedFloorplanMinPrice',
    value: number
  ) => void
}

type MapEntry<M extends Map<unknown, unknown>> = M extends Map<infer Key, infer Value>
  ? readonly [Key, Value]
  : unknown

type Option = MapEntry<ReturnType<typeof groupFloorplansByBedsBaths>>

function removeBullet<T extends string>(
  text: T
): T extends `${infer Start} · ${infer End}` ? `${Start} ${End}` : T {
  // @ts-expect-error TS can't tell we did the correct replacement
  return text.replace(' · ', ' ')
}

const getOptionText = (option: Option) => {
  const [bedsBathsText, { minPrice, maxPrice, allUnitsWaitListed }] = option
  const bedBathText = bedsBathsText.includes('Studio')
    ? 'Studio'
    : removeBullet(bedsBathsText).toLowerCase()
  const price = getPriceText({ maxPrice, minPrice, withSpace: false })
  const priceText = allUnitsWaitListed ? 'Ask to join waitlist' : price
  return { bedBathText, priceText } as const
}

const getDisplayValue = (option: Option | undefined) => {
  if (!option) return 'Select a floorplan'
  const { allUnitsWaitListed } = option[1]
  const { bedBathText, priceText } = getOptionText(option)
  return `${bedBathText} ${!allUnitsWaitListed ? priceText : ''}` as const
}

const getOptionLabel = (option: Option) => {
  const { bedBathText, priceText } = getOptionText(option)

  return (
    <Box<'div'> display="flex" justifyContent="space-between">
      <Box<'div'>>{bedBathText}</Box>
      <Box<'div'>>{priceText}</Box>
    </Box>
  )
}

const getOption = (option: Option) => {
  return {
    label: getOptionLabel(option),
    value: option[1].fpId,
    displayValue: getDisplayValue(option),
  } as const
}

export const getFloorplanIdFromOptionValue = ({
  floorplans,
  optionText,
}: {
  floorplans: ReadonlyArray<FloorplanListing<NormalFloorplanListingData>> | undefined
  optionText: string
}) => {
  if (!floorplans?.length) {
    return undefined
  }
  const value = optionText as `${ReturnType<typeof getOption>['value']}`
  return floorplans.find((fp) => value === `${fp.listingId}`)?.listingId || floorplans[0]?.listingId
}

export const FloorplanInput = ({
  error,
  floorplans,
  hasMessaged,
  isDefault,
  isInvalid,
  isMobile,
  isRequired,
  name = 'selectedFloorplanId',
  onChange,
  onlyShowFloorplanGroup,
  onTrackConversion,
  shouldChangeText,
  selectedFloorplanId,
  selectedFloorplanFromUrl,
  setFloorplanFromParam,
  setFieldValue,
}: Props) => {
  const [conversionTracked, setConversionTracked] = useState(false)
  const selectedFloorplan = floorplans?.find((floorplan) => floorplan.id === selectedFloorplanId)
  const floorplansDict = useMemo(() => createFloorplansIdDict(floorplans), [floorplans])
  const floorplansByBedsBaths = useMemo(
    () =>
      selectedFloorplanFromUrl
        ? groupFloorplansByBedsBaths(floorplans, selectedFloorplanFromUrl)
        : groupFloorplansByBedsBaths(floorplans, selectedFloorplan),
    [floorplans, selectedFloorplan, selectedFloorplanFromUrl]
  )

  const floorplanOptions = useMemo(
    () =>
      Array.from(floorplansByBedsBaths.entries())
        .filter(([_, value]) => isTruthy(value))
        .sort(sortOptions),
    [floorplansByBedsBaths]
  )

  const options = useMemo(() => floorplanOptions.map(getOption), [floorplanOptions])

  const handleChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    setFloorplanFromParam(undefined)
    if (!hasMessaged && !conversionTracked) {
      onTrackConversion?.(e)
      setConversionTracked(true)
    }
    onChange(e)
  }

  const value = useMemo(() => {
    if (!onlyShowFloorplanGroup && selectedFloorplanFromUrl) {
      return floorplanOptions.find(
        ([_, option]) => option.fpId === selectedFloorplanFromUrl?.listingId
      )
    } else if (!onlyShowFloorplanGroup && selectedFloorplan) {
      return floorplanOptions.find(([_, option]) => option.fpId === selectedFloorplanId)
    }

    const selectedBedsBathsTxt = selectedFloorplanFromUrl
      ? floorplansDict[selectedFloorplanFromUrl.id]
      : selectedFloorplanId == null
        ? undefined
        : floorplansDict[selectedFloorplanId]
    return floorplanOptions.find(([key]) => key === selectedBedsBathsTxt)
  }, [
    floorplanOptions,
    floorplansDict,
    onlyShowFloorplanGroup,
    selectedFloorplan,
    selectedFloorplanFromUrl,
    selectedFloorplanId,
  ])

  useEffect(() => {
    if (shouldChangeText) {
      const selectedBedsBathsTxt = selectedFloorplanFromUrl
        ? floorplansDict[selectedFloorplanFromUrl.id]
        : selectedFloorplanId == null
          ? undefined
          : floorplansDict[selectedFloorplanId]
      const floorplans =
        selectedBedsBathsTxt == null ? undefined : floorplansByBedsBaths.get(selectedBedsBathsTxt)

      if (floorplans) {
        setFieldValue('selectedFloorplanBedroom', floorplans.bedrooms)
        setFieldValue('selectedFloorplanMaxPrice', floorplans.maxPrice)
        setFieldValue('selectedFloorplanMinPrice', floorplans.minPrice)
      }
    }
  }, [
    floorplansByBedsBaths,
    floorplansDict,
    selectedFloorplanFromUrl,
    selectedFloorplan,
    setFieldValue,
    shouldChangeText,
    selectedFloorplanId,
  ])

  return (
    <InputWithPopover
      error={error}
      inputLabel="Floor plan"
      inputProps={{
        width: '100%',
      }}
      isDefault={isDefault}
      isInvalid={isInvalid}
      isMobile={isMobile}
      isRequired={isRequired}
      name={name}
      onChange={handleChange}
      placement="bottom-end"
      popoverHeader="FLOOR PLAN"
      popoverItems={options}
      value={getDisplayValue(value) ?? options[0]?.displayValue}
    />
  )
}
